import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import {
  LocalTaxi as TaxiIcon,
  CheckCircle as OkIcon,
  ErrorOutline as ErrorIcon,
  Schedule as ClockIcon
} from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

/**
 * Öffentliche Info-Seite hinter dem Event-QR-Code (Konzept §7.2).
 *
 * Gäste scannen den Aushang meist mit der normalen Kamera-App und landen hier.
 * Die Seite sagt nur, wer die Fahrt bezahlt und bis wann — bewusst ohne
 * Budgets, Grenzen oder Kundendaten. Kein Login nötig.
 */
const CompanyEventInfoPage = () => {
  const { code } = useParams();
  const [state, setState] = useState({ loading: true, info: null, error: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await httpsCallable(functions, 'getCompanyEventInfo')({ code });
        if (!cancelled) setState({ loading: false, info: res.data, error: false });
      } catch (e) {
        if (!cancelled) setState({ loading: false, info: null, error: true });
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  const { loading, info, error } = state;
  const unknown = error || !info?.found;

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      p: 2, bgcolor: 'background.default'
    }}>
      <Card sx={{ maxWidth: 460, width: '100%' }} elevation={3}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          {loading ? (
            <CircularProgress />
          ) : unknown ? (
            <Stack spacing={2} alignItems="center">
              <ErrorIcon color="disabled" sx={{ fontSize: 56 }} />
              <Typography variant="h6">Code nicht gefunden</Typography>
              <Typography variant="body2" color="text.secondary">
                Dieser Code gehört zu keinem gültigen Event. Bitte prüfen Sie den Aushang
                oder fragen Sie den Veranstalter.
              </Typography>
            </Stack>
          ) : info.running ? (
            <Stack spacing={2} alignItems="center">
              <TaxiIcon color="primary" sx={{ fontSize: 56 }} />
              <Typography variant="h5" fontWeight="bold">Ihre Taxifahrt ist bezahlt</Typography>
              <Typography variant="body1">
                {info.companyName} übernimmt die Fahrtkosten
                {info.name ? <> für <strong>{info.name}</strong></> : null}.
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
                <ClockIcon fontSize="small" />
                <Typography variant="body2">Gültig bis {fmt(info.validUntil)} Uhr</Typography>
              </Stack>
              <Box sx={{ textAlign: 'left', mt: 1 }}>
                <Typography variant="subtitle2" gutterBottom>So geht es:</Typography>
                <Stack spacing={0.75}>
                  <Typography variant="body2">1. Taxi wie gewohnt nehmen.</Typography>
                  <Typography variant="body2">2. Diesen Code dem Fahrer zeigen.</Typography>
                  <Typography variant="body2">3. Fahrtziel nennen — Sie zahlen nichts.</Typography>
                  <Typography variant="body2">4. Am Ziel kurz unterschreiben.</Typography>
                </Stack>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" color="success.main" sx={{ mt: 1 }}>
                <OkIcon fontSize="small" />
                <Typography variant="body2" fontWeight={600}>Event läuft</Typography>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2} alignItems="center">
              <ClockIcon color="disabled" sx={{ fontSize: 56 }} />
              <Typography variant="h6">
                {info.ended ? 'Dieses Event ist beendet' : 'Dieses Event hat noch nicht begonnen'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {info.name ? `„${info.name}" — ` : ''}
                {info.ended
                  ? `Gültig war sie bis ${fmt(info.validUntil)} Uhr.`
                  : `Gültig ab ${fmt(info.validFrom)} Uhr.`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fahrten außerhalb dieses Zeitraums zahlen Sie selbst.
              </Typography>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CompanyEventInfoPage;
